import { useEffect } from 'react';
import { post } from '../api/client';
import { useAuthStore } from '../stores/authStore';

/**
 * 邀请码全局捕获与绑定。
 *
 * 好友分享链接形如 `.../?ref=CODE`（Web BrowserRouter）或 `...#/?ref=CODE`（Native HashRouter）。
 * 无论落在哪个路由、登录与否，本 hook 都会：
 *   1. 从 URL 读取 `ref` 并暂存到 localStorage（匿名访客也能记住，登录后再绑定）；
 *   2. 清理地址栏中的 `ref` 参数（仅删该参数，保留 tab/checkout/plan 等其它 deep-link）；
 *   3. 用户登录后调用 POST /referral/bind 建立邀请关系。
 *
 * 反作弊/幂等由后端负责（禁止自荐、一人只能被邀请一次、同码重复绑定视为成功）。
 * 前端用模块级 attempted 集合避免同一会话重复请求；业务失败即清除暂存，网络失败保留待下次重试。
 *
 * 查询键约定：`ref` 只承载邀请推荐码（本 hook 消费）；推广短码用 `promo`（见管理端
 * pages/promotion-links 生成的分享链接），本 hook 不读 `promo`。两类归因码不共用同一个键，
 * 否则推广短码会被当成推荐码存进 pending_referral_code 并在绑定时被后端判为无效码丢弃。
 */

/** localStorage 暂存键：登录前捕获到的待绑定邀请码 */
const PENDING_KEY = 'pending_referral_code';

/** 本会话已尝试绑定过的邀请码，避免重复请求（登出再登入也不会重复轰炸后端） */
const attempted = new Set<string>();

/** 从 search 与 hash 两处读取 ref（兼容 BrowserRouter 与 HashRouter） */
function readRefFromUrl(): string | null {
  const { search, hash } = window.location;
  const fromSearch = new URLSearchParams(search).get('ref');
  if (fromSearch && fromSearch.trim()) return fromSearch.trim();
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    const fromHash = new URLSearchParams(hash.slice(qIndex + 1)).get('ref');
    if (fromHash && fromHash.trim()) return fromHash.trim();
  }
  return null;
}

/** 仅从地址栏删除 ref 参数，保留其它查询参数与 hash 路由 */
function stripRefFromUrl(): void {
  const { pathname, search, hash } = window.location;

  let nextSearch = search;
  const sp = new URLSearchParams(search);
  if (sp.has('ref')) {
    sp.delete('ref');
    const s = sp.toString();
    nextSearch = s ? `?${s}` : '';
  }

  let nextHash = hash;
  const qIndex = hash.indexOf('?');
  if (qIndex >= 0) {
    const hp = new URLSearchParams(hash.slice(qIndex + 1));
    if (hp.has('ref')) {
      hp.delete('ref');
      const hs = hp.toString();
      nextHash = hash.slice(0, qIndex) + (hs ? `?${hs}` : '');
    }
  }

  window.history.replaceState({}, '', `${pathname}${nextSearch}${nextHash}`);
}

function readPending(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

function writePending(code: string): void {
  try {
    localStorage.setItem(PENDING_KEY, code);
  } catch {
    /* 隐私模式下 localStorage 不可用，忽略 */
  }
}

function clearPending(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function useReferralCapture(): void {
  const userId = useAuthStore((s) => s.user?.id ?? null);

  useEffect(() => {
    // 1) 捕获 URL 中的 ?ref= 并暂存（渲染后的副作用，早于任何路由跳转丢失参数）
    const fromUrl = readRefFromUrl();
    if (fromUrl) {
      writePending(fromUrl);
      stripRefFromUrl();
    }

    // 2) 未登录则仅暂存，待登录后由本 effect 重跑时绑定
    if (!userId) return;

    const pending = readPending();
    if (!pending || attempted.has(pending)) return;
    attempted.add(pending);

    post('/referral/bind', { code: pending })
      .then(() => clearPending())
      .catch((e: unknown) => {
        // 业务失败（无效码/已绑他人/自荐/未启用）→ 清除暂存，避免反复尝试
        // 网络类错误保留暂存，下次挂载/登录后再试
        const isBusiness = (e as { isBusinessError?: boolean })?.isBusinessError === true;
        if (isBusiness) clearPending();
      });
  }, [userId]);
}
